import type {
    BimIndexOutcome,
    FileTotals,
    MinimapOutcome,
    OfflineError,
    OfflineSceneState,
    SyncedBimIndex,
    SyncedMinimap,
    SyncResult,
} from "@novorender/api";

import { telemetry } from "telemetry";
import { assertNever } from "utils/assertNever";

import type { OfflineSceneRecord } from "./offlineSlice";

/**
 * The widget's scene records, from the SDK's state and this tab's sync outcomes. What the widget
 * shows of a record is `offlineScreen.ts`'s.
 */

/**
 * When novoweb recorded a scene as last synced, before the SDK recorded it in the scene's sync
 * state record: the only date a scene synced before then has. Read-only; removed with the scene.
 */
export const legacySyncDates = {
    key: (sceneDirName: string) => `offline_scene/${sceneDirName}`,
    get(sceneDirName: string): string | undefined {
        const stored = localStorage.getItem(this.key(sceneDirName));

        if (!stored) {
            return;
        }

        try {
            const { lastSynced } = JSON.parse(stored) as { lastSynced?: unknown };
            return typeof lastSynced === "string" && lastSynced ? lastSynced : undefined;
        } catch (e) {
            console.warn(e);
            telemetry.trackCaughtException({
                source: "offline.useHandleOffline.getScene",
                category: "data",
                error: e,
            });
            localStorage.removeItem(this.key(sceneDirName));
        }
    },
    remove(sceneDirName: string): void {
        localStorage.removeItem(this.key(sceneDirName));
    },
    /** Every scene's, a scene left on disk or not. */
    removeAll(): void {
        const prefix = this.key("");
        Object.keys(localStorage)
            .filter((key) => key.startsWith(prefix))
            .forEach((key) => localStorage.removeItem(key));
    },
};

/**
 * The widget's record for a scene, from its durable state.
 * @param legacyLastSynced See {@link legacySyncDates}.
 * @returns `undefined` for a scene with nothing offline, which the widget doesn't list.
 */
export function sceneRecordFromState(
    id: string,
    state: OfflineSceneState,
    legacyLastSynced?: string,
): OfflineSceneRecord | undefined {
    switch (state.kind) {
        case "absent":
            return undefined;
        case "incremental":
        case "syncing":
        case "paused":
        case "interrupted":
        case "synced":
            return {
                id,
                view: state,
                ...(state.name !== undefined ? { name: state.name } : {}),
                ...(legacyLastSynced ? { legacyLastSynced } : {}),
            };
        default:
            return assertNever(state);
    }
}

/**
 * The scene's record once its state is read: {@link sceneRecordFromState}, with what novoweb
 * knows besides.
 * @param ownSync Whether a sync this tab began is still unsettled: a "syncing" state is then its
 *   own, and otherwise another tab's.
 * @param previous The record before: the capture read for a synced state carries over while the
 *   state is of the same sync.
 */
export function recordFromState(
    id: string,
    state: OfflineSceneState,
    ownSync: boolean,
    previous: OfflineSceneRecord | undefined,
): OfflineSceneRecord | undefined {
    const record = sceneRecordFromState(id, state, legacySyncDates.get(id));
    if (!record) {
        return undefined;
    }
    return {
        ...record,
        syncingElsewhere: state.kind === "syncing" && !ownSync,
        ...(state.kind === "synced" &&
        previous?.view.kind === "synced" &&
        previous.view.manifestId === state.manifestId &&
        previous.view.syncedAt === state.syncedAt &&
        previous.capturedManifestId !== undefined
            ? { capturedManifestId: previous.capturedManifestId }
            : {}),
    };
}

/**
 * How the outcome of this tab's sync changes the scene's record, as the result says it. A pause is
 * shown from the state read after it where that reads paused, which says more (the stopped sync's
 * target and name).
 * @param previous The record as the sync left it: its last progress tick's total is the stopped
 *   sync's target, as its record keeps it.
 */
export function sceneUpdateFromResult(
    result: SyncResult,
    previous: OfflineSceneRecord | undefined,
    now: Date,
): Partial<OfflineSceneRecord> {
    switch (result.kind) {
        case "completed":
            return {
                view: {
                    kind: "synced",
                    syncedAt: now.getTime(),
                    total: result.total,
                    // Not in the result: the state, read once the sync has settled, has it.
                    manifestId: undefined,
                    minimap: minimapFromOutcome(result.minimap),
                    bimIndex: bimIndexFromOutcome(result.bimIndex),
                },
            };
        case "paused": {
            const total = lastTickTotal(previous);
            return { view: { kind: "paused", downloaded: result.downloaded, ...(total ? { total } : {}) } };
        }
        case "failed":
            return { view: { kind: "failed", error: result.error, downloaded: result.downloaded } };
        default:
            return assertNever(result);
    }
}

/**
 * What an SDK call rejected with, as the typed error it is, or `unknown` for an unexpected throw.
 * The storage's own `QuotaExceededError` counts as `quotaExceeded`.
 */
export function asOfflineError(error: unknown): OfflineError {
    if (
        typeof error === "object" &&
        error !== null &&
        typeof (error as OfflineError).kind === "string" &&
        typeof (error as OfflineError).message === "string"
    ) {
        return error as OfflineError;
    }
    const message = error instanceof Error ? error.message : String(error);
    if (error instanceof Error && error.name === "QuotaExceededError") {
        return { kind: "quotaExceeded", message };
    }
    return { kind: "unknown", message };
}

function minimapFromOutcome(outcome: MinimapOutcome): SyncedMinimap {
    switch (outcome.kind) {
        case "notRequested":
            // The record keeps what an earlier sync left; the state, read next, says what.
            return { state: "unknown", total: undefined };
        case "synced":
            return { state: "synced", total: outcome.total };
        case "failed":
            return { state: "failed", total: outcome.total };
        default:
            return assertNever(outcome);
    }
}

function bimIndexFromOutcome(outcome: BimIndexOutcome): SyncedBimIndex {
    switch (outcome.kind) {
        case "absent":
            return { state: "absent" };
        case "stored":
            return { state: "stored", files: outcome.files, skipped: outcome.skipped.length };
        default:
            return assertNever(outcome);
    }
}

function lastTickTotal(record: OfflineSceneRecord | undefined): FileTotals | undefined {
    if (record?.view.kind === "syncing") {
        const progress = record.view.progress;
        if (progress?.bytes.total !== undefined && progress.files.total !== undefined) {
            return { bytes: progress.bytes.total, files: progress.files.total };
        }
    }
}
